import React, { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import FormInput from './FormInput';
import PhoneInput from './PhoneInput';
import SubmitButton from '../components/SubmitButton';
import { axiosInstance } from 'api/axios';
import { ENDPOINTS } from 'api/endpoints';
import SecureStorage from 'react-secure-storage';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import axios, { AxiosError } from 'axios';

const inputShellClass =
  'flex h-[52px] w-[3rem] sm:w-14 items-center justify-center rounded-xl bg-zinc-800/90 text-lg font-semibold text-white outline-none focus:ring-2 focus:ring-orange-500/50';

function RegisterLoginForm({ onClose, handleFetchData }) {
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const otpRefs = useRef([]);
  const [city, setCity] = useState('Unknown');
  const [latLon, setLatLon] = useState([0, 0]);
  const [locationGranted, setLocationGranted] = useState(false);

  useEffect(() => {
    const fetchCity = async (latitude, longitude) => {
      try {
        const response = await axios.get(
          `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=479a7e405e41d9a2196aef1b4d27a125`
        );
        setCity(response.data.name);
        setLatLon([latitude, longitude]);
        setLocationGranted(true);
      } catch (error) {
        console.error('Error fetching city:', error);
      }
    };

    if (!navigator.geolocation) return undefined;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        fetchCity(latitude, longitude);
      },
      () => {
        toast.error('Location permission is required to proceed.');
      }
    );

    return undefined;
  }, []);

  const loginMutate = useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(ENDPOINTS.LOGIN, data);
      return response.data;
    },
  });

  const verifyOtpMutate = useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(ENDPOINTS.VERIFY_OTP, data);
      return response.data;
    },
  });

  const loginFormik = useFormik({
    initialValues: {
      phoneCode: '+91',
      mobileNo: '',
    },
    validationSchema: Yup.object({
      mobileNo: Yup.string()
        .required('Mobile number is required')
        .matches(/^[0-9]+$/, 'Must be only digits')
        .min(10, 'Enter a valid 10-digit number')
        .max(10, 'Enter a valid 10-digit number'),
    }),
    onSubmit: (values) => {
      if (!locationGranted) {
        toast.error('Please allow location access to proceed.');
        return;
      }
      return loginMutate.mutateAsync(
        { ...values, city, latLon },
        {
          onSuccess: (data) => {
            setCustomerId(data.data._id);
            setShowOtpScreen(true);
            toast.success('OTP sent to your phone');
          },
          onError: (error) => {
            if (error instanceof AxiosError) {
              toast.error(error?.response?.data.message || 'Something went wrong');
            } else if (error.message) {
              toast.error(error.message);
            } else {
              toast.error('Something went wrong');
            }
          },
        }
      );
    },
  });

  const otpFormik = useFormik({
    initialValues: { otp: '' },
    validationSchema: Yup.object({
      otp: Yup.string().required('OTP is required').length(6, 'Enter the 6-digit OTP'),
    }),
    onSubmit: (values) => {
      verifyOtpMutate.mutate(
        { customerId, otp: values.otp, type: 'login' },
        {
          onSuccess: (data) => {
            SecureStorage.setItem('token', data.data.token);
            SecureStorage.setItem('customer', JSON.stringify(data.data.customer));
            toast.success('Logged in successfully');
            onClose?.();
            handleFetchData?.();
          },
          onError: (error) => {
            if (error instanceof AxiosError) {
              toast.error(error?.response?.data.message || 'Something went wrong');
            } else if (error.message) {
              toast.error(error.message);
            } else {
              toast.error('Something went wrong');
            }
          },
        }
      );
    },
  });

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;

    const otpArray = otpFormik.values.otp.split('');
    otpArray[index] = value;
    otpFormik.setFieldValue('otp', otpArray.join(''));

    if (value && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpFormik.values.otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  if (showOtpScreen) {
    return (
      <form className="flex flex-col mt-6 w-full" onSubmit={otpFormik.handleSubmit}>
        <h3 className="text-lg font-semibold text-white">Verify OTP</h3>
        <p className="mt-1 text-sm text-white/50">
          Code sent to {loginFormik.values.phoneCode} {loginFormik.values.mobileNo}
        </p>

        <div className="flex justify-between gap-2 sm:gap-2.5 mt-6" role="group" aria-label="One-time password">
          {[...Array(6)].map((_, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className={`${inputShellClass} sm:h-14 sm:w-12`}
              value={otpFormik.values.otp[index] || ''}
              onChange={(e) => handleOtpChange(e, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              onBlur={otpFormik.handleBlur}
              ref={(el) => {
                otpRefs.current[index] = el;
              }}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {otpFormik.touched.otp && otpFormik.errors.otp && (
          <p className="text-red-400 text-xs mt-2">{otpFormik.errors.otp}</p>
        )}

        <SubmitButton btnText="Verify & continue" isLoading={verifyOtpMutate.isPending} />

        <button
          type="button"
          className="mt-4 text-sm text-orange-400 hover:text-orange-300 transition-colors"
          onClick={() => setShowOtpScreen(false)}
        >
          Change mobile number
        </button>
      </form>
    );
  }

  return (
    <form className="flex flex-col mt-6 w-full" onSubmit={loginFormik.handleSubmit}>
      <label className="text-sm font-medium text-white/70 mb-2">Mobile number</label>
      <div className="flex gap-2 w-full">
        <PhoneInput countryCode={loginFormik.values.phoneCode} />
        <FormInput
          label="Mobile number"
          placeholder="10-digit number"
          name="mobileNo"
          value={loginFormik.values.mobileNo}
          onChange={loginFormik.handleChange}
          onBlur={loginFormik.handleBlur}
          error={loginFormik.touched.mobileNo && loginFormik.errors.mobileNo}
        />
      </div>

      {loginFormik.touched.mobileNo && loginFormik.errors.mobileNo && (
        <p className="text-red-400 text-xs mt-2">{loginFormik.errors.mobileNo}</p>
      )}

      {!locationGranted && (
        <p className="text-amber-400/90 text-xs mt-3">
          Allow location access in your browser to continue.
        </p>
      )}

      <SubmitButton
        btnText="Continue"
        disabled={!locationGranted}
        isLoading={loginMutate.isPending}
      />

      <p className="mt-5 text-xs leading-relaxed text-white/45">
        By continuing, you agree to our{' '}
        <span className="text-white/60">Terms &amp; Conditions</span> and{' '}
        <span className="text-white/60">Privacy Policy</span>.
      </p>

      <p className="mt-6 text-center text-[11px] text-white/30">
        © {new Date().getFullYear()} Travel booking
      </p>
    </form>
  );
}

RegisterLoginForm.propTypes = {
  onClose: PropTypes.func,
  handleFetchData: PropTypes.func,
};

export default RegisterLoginForm;
